import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { listRfqs } from '../../api/rfqs';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'in_review', label: 'In Review' },
  { value: 'awarded', label: 'Awarded' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'MMM d, yyyy');
  } catch {
    return value;
  }
};

const RFQListPage = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadRfqs = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await listRfqs(params);
      setRfqs(response.rfqs || []);
    } catch (err) {
      console.error('Failed to load RFQs', err);
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load RFQs. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRfqs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const tableRows = useMemo(() => rfqs || [], [rfqs]);

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Requests for Quote
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create sourcing bids, monitor partner responses, and prepare campaigns before launch.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton
            onClick={loadRfqs}
            disabled={loading}
            color="primary"
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/rfqs/new')}
          >
            New RFQ
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          size="small"
          sx={{ width: 200 }}
        >
          {STATUS_FILTERS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell align="right">Target Units</TableCell>
                <TableCell>Response Due</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No RFQs found. Create the first one to source your next campaign.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((rfq) => {
                  const id = rfq._id || rfq.id;
                  return (
                  <TableRow key={id}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {rfq.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {rfq.code || id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rfq.status}
                        size="small"
                        color={
                          rfq.status === 'issued'
                            ? 'primary'
                            : rfq.status === 'awarded'
                              ? 'success'
                              : rfq.status === 'cancelled'
                                ? 'error'
                                : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{rfq.brand?.name || '—'}</TableCell>
                    <TableCell>{rfq.campaign?.name || '—'}</TableCell>
                    <TableCell align="right">
                      {rfq.requirements?.production?.targetUnits ?? '—'}
                    </TableCell>
                    <TableCell>{formatDate(rfq.deadlines?.responseDue)}</TableCell>
                    <TableCell>{formatDate(rfq.updatedAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/rfqs/${id}`)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )})
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default RFQListPage;
